import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Search, Trash2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { customerAPI, customerPaymentAPI, dashboardAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import AddCustomerModal from '../components/modals/AddCustomerModal';
import { PageHeader } from '../components/PageHeader';
import { useAnalytics } from '../hooks/useAnalytics';
import { useDebounce } from '../hooks/useDebounce';
import { cn, formatDate, getInitials } from '../lib/utils';
import { useAuthStore } from '../store/auth';

export function CustomersPage() {
  const navigate = useNavigate()
  const { trackButtonClick } = useAnalytics()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [activeOnly, setActiveOnly] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading } = useQuery({
    queryKey: ['customers', debouncedSearch, offset, activeOnly],
    queryFn: () =>
      customerAPI
        .list({
          q: debouncedSearch || undefined,
          limit: 20,
          offset,
          active: activeOnly ? true : undefined,
        })
        .then((r) => r.data),
  })

  // Fetch expiry alerts to show what's expiring for each customer
  const { data: expiryData } = useQuery({
    queryKey: ['expiry-alerts-all'],
    queryFn: () =>
      dashboardAPI
        .getExpiryAlerts({ limit: 200 })
        .then((r) => r.data),
  })

  // Map expiry alerts to customers
  const expiryByCustomer = useMemo(() => {
    if (!expiryData?.alerts) return new Map<string, string[]>()
    
    const map = new Map<string, Set<string>>()
    expiryData.alerts.forEach((alert) => {
      if (!map.has(alert.customer_id)) {
        map.set(alert.customer_id, new Set())
      }
      const types = map.get(alert.customer_id)!
      // Format record type nicely
      const typeLabel = alert.record_type
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
      types.add(typeLabel)
    })
    
    // Convert Sets to sorted arrays
    const result = new Map<string, string[]>()
    map.forEach((types, customerId) => {
      result.set(customerId, Array.from(types).sort())
    })
    return result
  }, [expiryData])

  // Fetch payment summary for each customer
  const { data: paymentSummaries } = useQuery({
    queryKey: ['payment-summaries', data?.customers?.map(c => c.id).join(',')],
    queryFn: async () => {
      const summaries: Record<string, any> = {}
      if (data?.customers && data.customers.length > 0) {
        await Promise.all(
          data.customers.map(async (customer) => {
            try {
              const response = await customerPaymentAPI.getSummary(customer.id)
              summaries[customer.id] = response.data
            } catch (error) {
              summaries[customer.id] = null
            }
          })
        )
      }
      return summaries
    },
    enabled: !!data?.customers && data.customers.length > 0,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerAPI.delete(id),
    onSuccess: () => {
      toast.success('Customer deleted')
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setDeleteId(null)
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to delete customer',
      )
    },
  })

  const total = data?.total || 0
  const customers = data?.customers || []
  const totalPages = Math.ceil(total / 20)
  const currentPage = Math.floor(offset / 20) + 1

  return (
    <>
      <PageHeader
        title="Customers"
        action={{ 
          label: "Add Customer", 
          onClick: () => {
            trackButtonClick('Add Customer', { page: 'customers' })
            setShowAddModal(true)
          }
        }}
      />

      <div className="space-y-6">
        {/* Search and Filter */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or mobile no..."
              className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2 text-sm focus:border-saffron focus:outline-none"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setOffset(0)
              }}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => {
                trackButtonClick('Filter Active Customers', { filter: 'active' })
                setActiveOnly(true)
                setOffset(0)
              }}
              className={cn(
                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                activeOnly
                  ? 'bg-saffron text-white'
                  : 'border border-gray-200 text-gray-700 hover:bg-gray-50',
              )}
            >
              Active
            </button>
            <button
              onClick={() => {
                trackButtonClick('Filter Inactive Customers', { filter: 'inactive' })
                setActiveOnly(false)
                setOffset(0)
              }}
              className={cn(
                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                !activeOnly
                  ? 'bg-saffron text-white'
                  : 'border border-gray-200 text-gray-700 hover:bg-gray-50',
              )}
            >
              All
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Name & Phone
                </th>
                <th className="hidden px-6 py-3 text-left text-sm font-semibold text-gray-700 md:table-cell">
                  City / State
                </th>
                <th className="hidden px-6 py-3 text-left text-sm font-semibold text-gray-700 lg:table-cell">
                  Has Expiring
                </th>
                <th className="hidden px-6 py-3 text-left text-sm font-semibold text-gray-700 md:table-cell">
                  Pending Amount
                </th>
                <th className="hidden px-6 py-3 text-left text-sm font-semibold text-gray-700 lg:table-cell">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-200">
                    <td className="px-6 py-4">
                      <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="hidden px-6 py-4 md:table-cell">
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="hidden px-6 py-4 lg:table-cell">
                      <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="hidden px-6 py-4 lg:table-cell">
                      <div className="h-4 w-10 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="hidden px-6 py-4 md:table-cell">
                      <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="hidden px-6 py-4 md:table-cell">
                      <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
                    </td>
                  </tr>
                ))
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Users className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                    <p className="text-gray-500">
                      {debouncedSearch
                        ? 'No customers found matching your search.'
                        : 'No customers yet. Add your first customer.'}
                    </p>
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          trackButtonClick('View Customer', { customerId: customer.id, name: customer.full_name })
                          navigate(`/customers/${customer.id}`)
                        }}
                        className="group flex items-center gap-3"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                          {getInitials(customer.full_name)}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900 group-hover:text-saffron">
                            {customer.full_name}
                          </p>
                          <p className="text-sm text-gray-500">{customer.phone}</p>
                        </div>
                      </button>
                    </td>
                    <td className="hidden px-6 py-4 text-sm text-gray-700 md:table-cell">
                      {customer.city || '—'}
                      {customer.state ? ` / ${customer.state}` : ''}
                    </td>
                    <td className="hidden px-6 py-4 lg:table-cell">
                      {customer.has_expiring ? (
                        <div className="flex flex-wrap gap-1">
                          {(expiryByCustomer.get(customer.id) || []).map((type) => (
                            <span
                              key={type}
                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800"
                            >
                              <AlertCircle className="h-3 w-3" />
                              {type}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">—</div>
                      )}
                    </td>
                    <td className="hidden px-6 py-4 md:table-cell">
                      {paymentSummaries?.[customer.id]?.pending_amount !== undefined ? (
                        <span
                          className={
                            paymentSummaries[customer.id].pending_amount > 0
                              ? 'font-semibold text-red-600'
                              : 'font-semibold text-green-600'
                          }
                        >
                          ₹{paymentSummaries[customer.id].pending_amount.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                   
                    <td className="hidden px-6 py-4 text-sm text-gray-600 lg:table-cell">
                      {formatDate(customer.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            trackButtonClick('View Customer Details', { customerId: customer.id })
                            navigate(`/customers/${customer.id}`)
                          }}
                          className="text-saffron hover:underline"
                        >
                          View
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => {
                              trackButtonClick('Delete Customer', { customerId: customer.id })
                              setDeleteId(customer.id)
                            }}
                            className="text-danger hover:underline"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && customers.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-6 py-4">
            <span className="text-sm text-gray-600">
              Showing {offset + 1}–{Math.min(offset + 20, total)} of {total}{' '}
              customers
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => {
                  trackButtonClick('Pagination Previous', { page: currentPage, direction: 'previous' })
                  setOffset(Math.max(0, offset - 20))
                }}
              >
                Previous
              </Button>
              <span className="flex items-center px-3 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => {
                  trackButtonClick('Pagination Next', { page: currentPage, direction: 'next' })
                  setOffset(offset + 20)
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddCustomerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false)
          queryClient.invalidateQueries({ queryKey: ['customers'] })
          toast.success('Customer added successfully')
        }}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId)
        }}
        title="Delete Customer"
        description="This will permanently delete the customer and all their vehicles, licences, insurances, PUCs, fitness certificates, road tax and environmental tax records."
        confirmLabel="Delete"
        confirmVariant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  )
}

export default CustomersPage
